/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs"
import useConfig from "hooks/useConfig";
import { iaqStatusRangeInfo } from "data/data";
import { getAhuIaqLogsByQueryString } from "components/axios/ahuIaqLogAxios";

const DELAY_TIME =  5 * 60 * 1000;
// const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function bucketByHour(items) {
  const buckets = {};

  items.forEach(({ tsBucket, iaq }) => {
    if (!iaq?.displayIndex) return;

    const d = dayjs(tsBucket).tz(process.env.REACT_APP_TIMEZONE).startOf("hour");
    const key = d.toISOString();

    if (!buckets[key]) {
      buckets[key] = { sum: 0, count: 0, date: d };
    }

    buckets[key].sum += iaq.displayIndex;
    buckets[key].count += 1;
  });

  return Object.values(buckets)
    .sort((a, b) => a.date.valueOf() - b.date.valueOf())
    .map((b) => ({
      date: b.date,
      value: Math.round(b.sum / b.count), // 평균
    }));
}

function AirQualityIndexGraphs({infoId}) {
  const { t } = useTranslation();
  const { mode } = useConfig();

  const isLight = mode === "light";

  const textColor = isLight ? "#262626" : "#e5e7eb";
  const subTextColor = isLight ? "#6b7280" : "#9ca3af";
  const bgColor = isLight ? "#ffffff" : "#202020";
  const gridLineColor = isLight
    ? "rgba(0,0,0,0.08)"
    : "rgba(255,255,255,0.12)";

  /* ===============================
     1️⃣ API 상태
  =============================== */
  const [times, setTimes] = useState([]);
  const [iaqValues, setIaqValues] = useState([]);

  /* ===============================
     2️⃣ API 호출 (실시간)
  =============================== */
  useEffect(() => {
    let timer;

    const fetchIAQ = async () => {
      try {
        const res = await getAhuIaqLogsByQueryString(infoId, "?scale=144"); // ✅ 실제 API로 교체
        const values = res?.items || [];

        const hourly = bucketByHour(values);

        // const pad = (n) => (n < 10 ? "0" : "") + n;
        // const fmt = (d) =>
        // `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:00`;

        setTimes(hourly.map(v => v.date.format("MM-DD HH:00")));
        setIaqValues(hourly.map(v => v.value));

      } catch (e) {
        console.error("IAQ API error", e);
      }
    };

    fetchIAQ();
    timer = setInterval(fetchIAQ, DELAY_TIME); 

    return () => clearInterval(timer);
  }, [infoId]);

  /* ===============================
     3️⃣ 상태 계산
  =============================== */
  const currentIAQ = iaqValues[iaqValues.length - 1];
  const currentLabel =
    currentIAQ != null ? t(iaqStatusRangeInfo(currentIAQ).name) : "-";

  const statusColorMap = {
    [t("message.common.text.very.good")]: "#2ecc71",
    [t("message.common.text.well")]: "#a3d977",
    [t("message.common.text.average")]: "#f1c40f",
    [t("message.common.text.bad")]: "#e74c3c",
  };

  const option = useMemo(() => {
    // if (!iaqValues.length) return {};

    // /* ===============================
    //    1️⃣ 더미 데이터 생성부 (API 붙이면 제거)
    // =============================== */
    // const now = new Date();

    // const pad = (n) => (n < 10 ? "0" : "") + n;
    // const fmt = (d) =>
    //   `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    //     d.getHours()
    //   )}:00`;

    // const times = [];
    // const iaqValues = [];
    // let base = 78;

    // for (let i = 23; i >= 0; i--) {
    //   const t = new Date(now.getTime() - i * 3600 * 1000);
    //   times.push(fmt(t));

    //   const h = t.getHours();
    //   const event =
    //     h >= 12 && h <= 14 ? -10 :
    //     h >= 18 && h <= 20 ? -8 : 0;

    //   base = base + (Math.random() * 6 - 3) + event * 0.15;
    //   base = Math.max(20, Math.min(98, base));
    //   iaqValues.push(Math.round(base));
    // }

    // // 마지막 값 85 보정
    // const TARGET = 85;
    // const offset = TARGET - iaqValues[iaqValues.length - 1];
    // for (let i = 0; i < iaqValues.length; i++) {
    //   iaqValues[i] = Math.max(0, Math.min(100, iaqValues[i] + offset));
    // }
    // iaqValues[iaqValues.length - 1] = TARGET;

    // /* ===============================
    //    2️⃣ IAQ 상태 계산
    // =============================== */
    // const iaqStatus = (v) => {
    //   if (v > 85) return t("message.common.text.very.good");
    //   if (v > 70) return t("message.common.text.well");
    //   if (v > 55) return t("message.common.text.average");
    //   return t("message.common.text.bad");
    // };

    // const statusColorMap = {
    //   [t("message.common.text.very.good")]: "#2ecc71",
    //   [t("message.common.text.well")]: "#a3d977",
    //   [t("message.common.text.average")]: "#f1c40f",
    //   [t("message.common.text.bad")]: "#e74c3c",
    // };

    // const currentIAQ = iaqValues[iaqValues.length - 1];
    // const currentLabel = iaqStatus(currentIAQ);
    
    /* ===============================
       3️⃣ ECharts option
    =============================== */
    return {
      backgroundColor: bgColor,
      textStyle: { color: textColor },

      title: {
        text: `${t("message.common.chip.air.quality.index.graph")} (IAQ)`,
        subtext: `${t("message.common.text.current.value")} : ${currentIAQ} / 100 (${currentLabel})`,
        left: "center",
        top: '10px',
        textStyle: { color: textColor, fontSize: 20 },
        subtextStyle: { color: subTextColor },
      },

      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const v = params[0].data;
          return `
            ${params[0].axisValue}<br/>
            IAQ: <b>${v}</b> / 100<br/>
            ${t("message.common.subtitle.status")}: <b>${t(iaqStatusRangeInfo(v).name)}</b>
          `;
        },
      },

      legend: {
        top: 62,
        left: "center",
        selectedMode: false,
        icon: "roundRect",
        itemWidth: 34,
        itemHeight: 14,
        itemGap: 18,
        textStyle: { color: subTextColor },
        data: Object.keys(statusColorMap),
      },

      grid: { left: 60, right: 40, top: 130, bottom: 55 },

      xAxis: {
        type: "category",
        boundaryGap: false,
        data: times,
        axisLabel: { 
            interval: 3, 
            // rotate: 30, 
            color: subTextColor 
        },
        axisLine: { lineStyle: { color: gridLineColor } },
      },

      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: { color: subTextColor },
        splitLine: { lineStyle: { color: gridLineColor } },
      },

      visualMap: {
        show: false,
        pieces: Object.entries(statusColorMap).map(([label, color]) => ({
          label,
          color,
          ...(label === t("message.common.text.bad")
            ? { lte: 55 }
            : label === t("message.common.text.average")
            ? { gt: 55, lte: 70 }
            : label === t("message.common.text.well")
            ? { gt: 70, lte: 85 }
            : { gt: 85 }),
        })),
      },

      series: [
        // 범례용 더미 시리즈
        ...Object.entries(statusColorMap).map(([name, color]) => ({
          name,
          type: "line",
          data: [],
          showSymbol: false,
          lineStyle: { color, width: 8 },
          tooltip: { show: false },
        })),

        // 실제 IAQ 시계열
        {
          name: "IAQ",
          type: "line",
          data: iaqValues,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: { width: 4 },
          areaStyle: { opacity: 0.1 },
        },
      ],
    };
//   }, [mode, t]);
  }, [iaqValues, times, mode, t]);

  // useEffect(() => {
  //   console.log("ECharts option updated", option);
  // }, [option]);

  if (!times.length || !iaqValues.length) {
    return null; // 또는 placeholder UI
  }

  return (
    <div style={{ width: "100%", height: 360 }}>
      <ReactECharts
        option={option}
        style={{ width: "100%", height: "100%" }}
        notMerge
        lazyUpdate
      />
    </div>
  );
}

export default AirQualityIndexGraphs;
